import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, AlertTriangle, TrendingUp, Globe, Fuel, Building2, ChevronRight, ChevronDown, Info, Activity, ArrowLeft, PlayCircle, FileText, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDemoMode } from '@/contexts/DemoModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimulationPanel = ({ token }) => {
  const { checkDemoBlock } = useDemoMode();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [result, setResult] = useState(null);
  const [allResults, setAllResults] = useState(null);
  const [error, setError] = useState(null);
  const [iterations, setIterations] = useState(500);
  const [expandedSection, setExpandedSection] = useState('scenario');

  useEffect(() => {
    fetchTemplates();
  }, [token]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/simulation/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const runSimulation = async () => {
    if (!selectedTemplate) return;
    if (checkDemoBlock('Simulation Monte Carlo')) return;
    
    setIsRunning(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/simulation/run`,
        {
          template_id: selectedTemplate,
          monte_carlo_iterations: iterations
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setExpandedSection('results');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la simulation');
    } finally {
      setIsRunning(false);
    }
  };

  const runAllSimulations = async () => {
    setIsRunningAll(true);
    setError(null);
    setAllResults(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/simulation/run-all`,
        { monte_carlo_iterations: iterations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllResults(response.data);
      setExpandedSection('all-results');
      toast.success(`${response.data.successful}/${response.data.total_templates} simulations réussies`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors des simulations');
      toast.error('Erreur lors des simulations');
    } finally {
      setIsRunningAll(false);
    }
  };

  const generatePdfReport = async () => {
    setIsGeneratingPdf(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/simulation/report/pdf`,
        { 
          simulation_results: allResults?.results,
          monte_carlo_iterations: iterations 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        // Download the PDF with authentication
        const pdfUrl = `${API_URL}${response.data.url}`;
        const pdfResponse = await axios.get(pdfUrl, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        // Create blob URL and open in new tab
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Revoke after delay to allow tab to load
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        
        toast.success('Rapport PDF généré avec succès');
      }
    } catch (err) {
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const resetToScenarioSelection = () => {
    setResult(null);
    setAllResults(null);
    setError(null);
    setExpandedSection('scenario');
  };

  const getExposureBadgeColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'CRITIQUE': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'ÉLEVÉ': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'MODÉRÉ': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default: return 'bg-green-500/20 text-green-400 border-green-500/50';
    }
  };

  const getSeverityColor = (severity) => {
    if (severity >= 4) return 'text-red-400';
    if (severity >= 3) return 'text-orange-400';
    return 'text-yellow-400';
  };

  const theaterIcons = {
    'Hormuz': '🛢️',
    'Red Sea': '🚢',
    'Suez': '⚓',
    'Malacca': '🌊',
    'Black Sea': '🌑',
    'Persian Gulf': '🔥'
  };

  // Mapping pour afficher les noms en français
  const theaterDisplayNames = {
    'Hormuz': 'Ormuz',
    'Red Sea': 'Mer Rouge',
    'Suez': 'Suez',
    'Malacca': 'Malacca',
    'Black Sea': 'Mer Noire',
    'Persian Gulf': 'Golfe Persique'
  };

  return (
    <div className="space-y-4" data-testid="simulation-panel">
      {/* Template Selection */}
      <motion.div 
        className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button 
          onClick={() => setExpandedSection(expandedSection === 'scenario' ? null : 'scenario')}
          className="w-full flex items-center justify-between text-cyan-400 font-mono font-bold mb-3"
          data-testid="scenario-section-toggle"
        >
          <span className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            SÉLECTION DU SCÉNARIO
          </span>
          {expandedSection === 'scenario' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        
        <AnimatePresence>
          {expandedSection === 'scenario' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {templates.map((template) => (
                  <button
                    key={template.template_id}
                    onClick={() => setSelectedTemplate(template.template_id)}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      selectedTemplate === template.template_id
                        ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                        : 'bg-slate-700/30 border-slate-600/50 text-slate-300 hover:border-cyan-500/50'
                    }`}
                    data-testid={`template-${template.template_id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span>{theaterIcons[template.theater] || '🌍'}</span>
                      <span className="font-mono text-xs text-cyan-400">{theaterDisplayNames[template.theater] || template.theater}</span>
                      <span className={`text-xs ${getSeverityColor(template.severity_range?.[1] || 3)}`}>
                        S{template.severity_range?.[0]}-{template.severity_range?.[1]}
                      </span>
                    </div>
                    <div className="font-medium text-sm">{template.name}</div>
                    <div className="text-xs text-slate-400 mt-1 line-clamp-2">
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Monte Carlo iterations slider with explanation */}
              <div className="mb-4 p-3 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400 font-mono flex items-center gap-2">
                    <Info className="w-3 h-3" />
                    ITÉRATIONS MONTE CARLO
                  </label>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setIterations(200)}
                      className={`px-2 py-1 text-xs font-mono rounded ${iterations <= 200 ? 'bg-green-500/30 text-green-400 border border-green-500/50' : 'bg-slate-600/30 text-slate-400 hover:bg-slate-600/50'}`}
                    >
                      RAPIDE
                    </button>
                    <button
                      onClick={() => setIterations(500)}
                      className={`px-2 py-1 text-xs font-mono rounded ${iterations > 200 && iterations <= 700 ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50' : 'bg-slate-600/30 text-slate-400 hover:bg-slate-600/50'}`}
                    >
                      STANDARD
                    </button>
                    <button
                      onClick={() => setIterations(1000)}
                      className={`px-2 py-1 text-xs font-mono rounded ${iterations > 700 && iterations <= 1200 ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50' : 'bg-slate-600/30 text-slate-400 hover:bg-slate-600/50'}`}
                    >
                      PRÉCIS
                    </button>
                    <button
                      onClick={() => setIterations(2000)}
                      className={`px-2 py-1 text-xs font-mono rounded ${iterations > 1200 ? 'bg-red-500/30 text-red-400 border border-red-500/50' : 'bg-slate-600/30 text-slate-400 hover:bg-slate-600/50'}`}
                    >
                      EXPERT
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mb-3">
                  <input
                    type="range"
                    min="100"
                    max="2000"
                    step="100"
                    value={iterations}
                    onChange={(e) => setIterations(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    data-testid="iterations-slider"
                  />
                  <span className="text-cyan-400 font-mono text-lg font-bold w-16">{iterations}</span>
                </div>
                
                {/* Explanation based on selected iterations */}
                <div className="text-xs text-slate-400 bg-slate-800/50 p-2 rounded">
                  <p className="mb-1">
                    <span className="text-cyan-400 font-bold">Méthode Monte Carlo :</span> Simulation probabiliste qui exécute le modèle {iterations} fois avec des paramètres aléatoires pour estimer la distribution des résultats possibles.
                  </p>
                  <p className="text-slate-500">
                    {iterations <= 200 && "⚡ Mode rapide (~2s) : Estimation grossière, intervalles de confiance larges. Idéal pour un aperçu rapide."}
                    {iterations > 200 && iterations <= 700 && "✓ Mode standard (~5s) : Bon compromis précision/vitesse. Recommandé pour la plupart des analyses."}
                    {iterations > 700 && iterations <= 1200 && "📊 Mode précis (~10s) : Intervalles de confiance resserrés. Pour des rapports officiels."}
                    {iterations > 1200 && "🎯 Mode expert (~20s) : Précision maximale. Pour des analyses stratégiques approfondies."}
                  </p>
                </div>
              </div>
              
              {/* Run button */}
              <button
                onClick={runSimulation}
                disabled={!selectedTemplate || isRunning || isRunningAll}
                className={`w-full py-3 rounded-lg font-mono font-bold flex items-center justify-center gap-2 transition-all ${
                  selectedTemplate && !isRunning && !isRunningAll
                    ? 'bg-cyan-500/20 border border-cyan-500 text-cyan-400 hover:bg-cyan-500/30'
                    : 'bg-slate-700/30 border border-slate-600 text-slate-500 cursor-not-allowed'
                }`}
                data-testid="run-simulation-btn"
              >
                {isRunning ? (
                  <>
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Activity className="w-4 h-4" />
                    </motion.div>
                    SIMULATION EN COURS...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    LANCER LA SIMULATION
                  </>
                )}
              </button>

              {/* Run All Scenarios button */}
              <button
                onClick={runAllSimulations}
                disabled={isRunning || isRunningAll}
                className={`w-full py-3 mt-2 rounded-lg font-mono font-bold flex items-center justify-center gap-2 transition-all ${
                  !isRunning && !isRunningAll
                    ? 'bg-amber-500/20 border border-amber-500 text-amber-400 hover:bg-amber-500/30'
                    : 'bg-slate-700/30 border border-slate-600 text-slate-500 cursor-not-allowed'
                }`}
                data-testid="run-all-simulations-btn"
              >
                {isRunningAll ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    SIMULATIONS EN COURS...
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-4 h-4" />
                    LANCER TOUS LES SCÉNARIOS
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Error display */}
      {error && (
        <motion.div 
          className="bg-red-500/10 border border-red-500/50 rounded-lg p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-mono">{error}</span>
          </div>
        </motion.div>
      )}

      {/* Results display */}
      {result && result.simulation && (
        <motion.div 
          className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button 
            onClick={() => setExpandedSection(expandedSection === 'results' ? null : 'results')}
            className="w-full flex items-center justify-between text-cyan-400 font-mono font-bold mb-3"
            data-testid="results-section-toggle"
          >
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              RÉSULTATS DE LA SIMULATION
            </span>
            {expandedSection === 'results' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expandedSection === 'results' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-4"
              >
                {/* Scenario info */}
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <div className="text-xs text-slate-400 font-mono mb-1">SCÉNARIO</div>
                  <div className="text-lg font-bold text-white">
                    {result.simulation.scenario?.name}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-slate-400">
                      Théâtre: <span className="text-cyan-400">{theaterDisplayNames[result.simulation.scenario?.theater] || result.simulation.scenario?.theater}</span>
                    </span>
                    <span className="text-slate-400">
                      Durée: <span className="text-cyan-400">{result.simulation.scenario?.duration_days}j</span>
                    </span>
                    <span className="text-slate-400">
                      Sévérité: <span className={getSeverityColor(result.simulation.scenario?.severity)}>
                        {result.simulation.scenario?.severity}/5
                      </span>
                    </span>
                  </div>
                </div>
                
                {/* Price impacts */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Brent */}
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Fuel className="w-4 h-4 text-orange-400" />
                      <span className="text-xs text-slate-400 font-mono">PÉTROLE BRENT</span>
                    </div>
                    <div className="text-2xl font-bold text-white">
                      ${result.simulation.results?.brent?.p50?.toFixed(0)}
                      <span className="text-sm text-red-400 ml-2">
                        +{result.simulation.results?.brent?.delta_pct_p50?.toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      P10: ${result.simulation.results?.brent?.p10?.toFixed(0)} | 
                      P90: ${result.simulation.results?.brent?.p90?.toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Baseline: ${result.simulation.results?.brent?.baseline?.toFixed(0)}/baril
                    </div>
                  </div>
                  
                  {/* TTF */}
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-slate-400 font-mono">GAZ TTF</span>
                    </div>
                    <div className="text-2xl font-bold text-white">
                      €{result.simulation.results?.ttf?.p50?.toFixed(0)}
                      <span className="text-sm text-red-400 ml-2">
                        +{result.simulation.results?.ttf?.delta_pct_p50?.toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      P10: €{result.simulation.results?.ttf?.p10?.toFixed(0)} | 
                      P90: €{result.simulation.results?.ttf?.p90?.toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Baseline: €{result.simulation.results?.ttf?.baseline?.toFixed(0)}/MWh
                    </div>
                  </div>
                </div>
                
                {/* France exposure */}
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-mono">IMPACT FRANCE</span>
                    <span className={`px-2 py-1 rounded text-xs font-bold border ${getExposureBadgeColor(result.simulation.results?.france?.exposure_level)}`}>
                      {result.simulation.results?.france?.exposure_level}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-slate-400 text-xs">Surcoût estimé</div>
                      <div className="text-white font-bold">
                        {result.simulation.results?.france?.cost_impact_eur_billions?.p10?.toFixed(1)} - 
                        {result.simulation.results?.france?.cost_impact_eur_billions?.p90?.toFixed(1)} Mds €
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs">Réserves après scénario</div>
                      <div className="text-white font-bold">
                        {result.simulation.results?.france?.reserves_after?.p50?.toFixed(0)} jours
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Top drivers */}
                {result.simulation.analysis?.top_drivers?.length > 0 && (
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <div className="text-xs text-slate-400 font-mono mb-2">FACTEURS CLÉS</div>
                    <ul className="space-y-1">
                      {result.simulation.analysis.top_drivers.slice(0, 3).map((driver, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-cyan-400">▸</span>
                          {driver}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Recommendations */}
                {result.simulation.analysis?.recommendations?.length > 0 && (
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <div className="text-xs text-slate-400 font-mono mb-2">RECOMMANDATIONS</div>
                    <ul className="space-y-1">
                      {result.simulation.analysis.recommendations.slice(0, 4).map((rec, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-yellow-400">⚠</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Alerts */}
                {result.simulation_alerts?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <div className="text-xs text-red-400 font-mono mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" />
                      ALERTES DÉCLENCHÉES ({result.alerts_triggered})
                    </div>
                    <ul className="space-y-1">
                      {result.simulation_alerts.map((alert, i) => (
                        <li key={i} className="text-sm text-red-300">
                          {alert.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Disclaimers */}
                <div className="flex items-start gap-2 p-2 bg-slate-900/50 rounded text-xs text-slate-500">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Modèle heuristique basé sur {result.simulation.metadata?.iterations} itérations Monte Carlo. 
                    Les résultats sont des ordres de grandeur, non des prévisions.
                  </span>
                </div>

                {/* Back button */}
                <button
                  onClick={resetToScenarioSelection}
                  className="w-full py-2 mt-4 rounded-lg font-mono text-sm flex items-center justify-center gap-2 bg-slate-700/30 border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-all"
                  data-testid="back-to-scenarios-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  RETOUR À LA SÉLECTION DES SCÉNARIOS
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* All Results display */}
      {allResults && (
        <motion.div 
          className="bg-slate-800/50 border border-amber-500/30 rounded-lg p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button 
            onClick={() => setExpandedSection(expandedSection === 'all-results' ? null : 'all-results')}
            className="w-full flex items-center justify-between text-amber-400 font-mono font-bold mb-3"
            data-testid="all-results-section-toggle"
          >
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              RÉSULTATS MULTI-SCÉNARIOS ({allResults.successful}/{allResults.total_templates})
            </span>
            {expandedSection === 'all-results' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expandedSection === 'all-results' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-3"
              >
                {/* Summary table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-600">
                        <th className="text-left py-2 px-2 text-slate-400 font-mono text-xs">SCÉNARIO</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-mono text-xs">THÉÂTRE</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-mono text-xs">BRENT P50</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-mono text-xs">GAZ P50</th>
                        <th className="text-center py-2 px-2 text-slate-400 font-mono text-xs">IMPACT FR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allResults.results?.filter(r => r.status === 'success').map((r, idx) => (
                        <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-2 px-2 text-white text-xs">{r.template_name}</td>
                          <td className="py-2 px-2 text-center text-cyan-400 text-xs">{theaterDisplayNames[r.theater] || r.theater}</td>
                          <td className="py-2 px-2 text-center text-orange-400 font-mono text-xs">
                            ${r.simulation?.results?.brent?.p50?.toFixed(0) || 'N/A'}
                          </td>
                          <td className="py-2 px-2 text-center text-blue-400 font-mono text-xs">
                            €{r.simulation?.results?.ttf?.p50?.toFixed(0) || 'N/A'}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getExposureBadgeColor(r.simulation?.results?.france?.exposure_level)}`}>
                              {r.simulation?.results?.france?.exposure_level || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Failed simulations */}
                {allResults.failed > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                    <div className="text-xs text-red-400 font-mono">
                      {allResults.failed} simulation(s) échouée(s)
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={generatePdfReport}
                    disabled={isGeneratingPdf}
                    className="flex-1 py-3 rounded-lg font-mono font-bold flex items-center justify-center gap-2 bg-green-500/20 border border-green-500 text-green-400 hover:bg-green-500/30 transition-all disabled:opacity-50"
                    data-testid="generate-pdf-report-btn"
                  >
                    {isGeneratingPdf ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        GÉNÉRATION EN COURS...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        GÉNÉRER RAPPORT PDF
                      </>
                    )}
                  </button>
                </div>

                {/* Back button */}
                <button
                  onClick={resetToScenarioSelection}
                  className="w-full py-2 rounded-lg font-mono text-sm flex items-center justify-center gap-2 bg-slate-700/30 border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-all"
                  data-testid="back-to-scenarios-btn-all"
                >
                  <ArrowLeft className="w-4 h-4" />
                  RETOUR À LA SÉLECTION
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
};

export default SimulationPanel;
